/**
 * Investors List Page Component
 * /admin/investors
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import apiClient from '../api/client';

const Investors = () => {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadInvestors();
  }, []);

  const loadInvestors = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.getInvestors({ search: searchTerm });
      // Handle paginated response
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setInvestors(data);
    } catch (err) {
      setError(err.message || 'Failed to load investors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadInvestors();
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'default',
      suspended: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading && investors.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom>
        Investors
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <form onSubmit={handleSearchSubmit}>
          <TextField
            fullWidth
            placeholder="Search investors by name, email, phone..."
            value={searchTerm}
            onChange={handleSearch}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            } }}
          />
        </form>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Emirate ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Vehicles</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {investors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No investors found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              investors.map((investor) => (
                <TableRow key={investor.id} hover>
                  <TableCell>
                    {investor.first_name} {investor.last_name}
                  </TableCell>
                  <TableCell>{investor.email || '-'}</TableCell>
                  <TableCell>{investor.phone || '-'}</TableCell>
                  <TableCell>{investor.emirate_id || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={investor.status || 'active'}
                      color={getStatusColor(investor.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{investor.vehicle_count || 0}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Investors;

